/*
 * Wishlist Choon — capa de tema sobre el SDK de Swym (Wishlist Plus).
 *
 * El App Embed de Swym carga `window._swat`. Este archivo NO renderiza los
 * botones de Swym: el tema dibuja sus propios corazones (<wishlist-button>) y
 * usa el SDK para leer y escribir la lista.
 *
 * API de Swym usada:
 *   _swat.fetch(ok, err)                      -> [{ empi, epi, du, ... }]
 *   _swat.addToWishList({empi,epi,du}, ok, err)
 *   _swat.removeFromWishList({empi,epi,du}, ok, err)
 */
(function () {
  'use strict';

  var STRINGS = window.ChoonWishlistStrings || {};
  var LOGIN_URL = STRINGS.loginUrl || '/account/login';

  /* ------------------------------------------------------------------ *
   * Estado compartido: una sola lectura de la wishlist por carga.
   * ------------------------------------------------------------------ */
  var store = {
    ready: false,
    error: false,
    products: new Map(),
    // empi (id de producto) -> Set de epi (ids de variante) guardados
    items: new Map(),
    subscribers: new Set(),
    pending: null
  };

  function onSwymReady(callback) {
    if (window._swat && window._swat.retailerSettings) {
      callback(window._swat);
      return;
    }
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  }

  function isEnabled(swat) {
    try {
      var app = swat.getApp('Wishlist');
      return !!(app && app.enabled);
    } catch (e) {
      return false;
    }
  }

  function indexItems(list) {
    var map = new Map();
    (list || []).forEach(function (item) {
      if (!item || item.empi == null) return;
      var key = String(item.empi);
      if (!map.has(key)) map.set(key, new Set());
      if (item.epi != null) map.get(key).add(String(item.epi));
    });
    return map;
  }

  function notify() {
    store.subscribers.forEach(function (fn) {
      try {
        fn(store);
      } catch (e) {
        /* no romper el resto de botones */
      }
    });
    updateHeaderCount();
  }

  function load(force) {
    if (store.pending && !force) return store.pending;
    store.pending = new Promise(function (resolve) {
      onSwymReady(function (swat) {
        if (!isEnabled(swat)) {
          store.ready = true;
          store.error = true;
          notify();
          resolve(store);
          return;
        }
        swat.fetch(
          function (list) {
            store.items = indexItems(list);
            store.products = new Map();
            (list || []).forEach(function (item) {
              if (item && item.empi != null) store.products.set(String(item.empi), item);
            });
            store.ready = true;
            store.error = false;
            notify();
            resolve(store);
          },
          function () {
            store.ready = true;
            store.error = true;
            notify();
            resolve(store);
          }
        );
      });
    });
    return store.pending;
  }

  function totalCount() {
    var total = 0;
    store.items.forEach(function (variants) {
      total += variants.size || 1;
    });
    return total;
  }

  function hasProduct(productId) {
    var set = store.items.get(String(productId));
    return !!(set && set.size);
  }

  function variantsOf(productId) {
    var set = store.items.get(String(productId));
    return set ? Array.from(set) : [];
  }

  function remember(productId, variantId) {
    var key = String(productId);
    if (!store.items.has(key)) store.items.set(key, new Set());
    if (variantId != null) store.items.get(key).add(String(variantId));
  }

  function forget(productId, variantId) {
    var key = String(productId);
    var set = store.items.get(key);
    if (!set) return;
    if (variantId == null) {
      store.items.delete(key);
      return;
    }
    set.delete(String(variantId));
    if (!set.size) store.items.delete(key);
  }

  /* ------------------------------------------------------------------ *
   * Contador del icono de cabecera
   * ------------------------------------------------------------------ */
  function updateHeaderCount() {
    var total = totalCount();
    document.querySelectorAll('.wishlist-total-count').forEach(function (node) {
      node.setAttribute('data-total', String(total));
      node.textContent = String(total);
      node.hidden = total === 0;
    });
  }

  /* ------------------------------------------------------------------ *
   * Operaciones
   * ------------------------------------------------------------------ */
  function addItem(payload) {
    return new Promise(function (resolve, reject) {
      onSwymReady(function (swat) {
        if (!isEnabled(swat)) {
          reject(new Error('wishlist-disabled'));
          return;
        }
        swat.addToWishList(
          payload,
          function (response) {
            remember(payload.empi, payload.epi);
            store.products.set(String(payload.empi), Object.assign({}, payload, response));
            notify();
            resolve(response);
          },
          reject
        );
      });
    });
  }

  function removeItem(payload) {
    return new Promise(function (resolve, reject) {
      onSwymReady(function (swat) {
        if (!isEnabled(swat)) {
          reject(new Error('wishlist-disabled'));
          return;
        }
        swat.removeFromWishList(
          payload,
          function (response) {
            forget(payload.empi, payload.epi);
            notify();
            resolve(response);
          },
          reject
        );
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * <wishlist-button>
   * ------------------------------------------------------------------ */
  var LABEL_ADD = 'Añadir a favoritos';
  var LABEL_REMOVE = 'Quitar de favoritos';

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.button = this.querySelector('button') || this;
      this.labelAdd = this.dataset.labelAdd || LABEL_ADD;
      this.labelRemove = this.dataset.labelRemove || LABEL_REMOVE;

      this._onStoreChange = this.render.bind(this);
      store.subscribers.add(this._onStoreChange);

      this.button.addEventListener('click', this.onClick.bind(this));
      this.render();
      load();
    }

    disconnectedCallback() {
      store.subscribers.delete(this._onStoreChange);
    }

    get productId() {
      return this.dataset.productId;
    }

    get variantId() {
      // La PDP actualiza data-variant-id al cambiar de variante.
      return this.dataset.variantId;
    }

    get productUrl() {
      return this.dataset.productUrl || window.location.origin + '/products/' + (this.dataset.handle || '');
    }

    get isAdded() {
      return hasProduct(this.productId);
    }

    render() {
      var added = this.isAdded;
      this.classList.toggle('is-added', added);
      if (this.button !== this) this.button.classList.toggle('is-added', added);
      this.button.setAttribute('aria-pressed', added ? 'true' : 'false');
      this.button.setAttribute('aria-label', added ? this.labelRemove : this.labelAdd);
      this.button.setAttribute('title', added ? this.labelRemove : this.labelAdd);
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.button.disabled = busy;
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.classList.contains('is-busy')) return;

      var productId = parseInt(this.productId, 10);
      var variantId = parseInt(this.variantId, 10);
      if (!productId) return;

      var payload = {
        empi: productId,
        epi: variantId || undefined,
        du: this.productUrl
      };

      var self = this;
      this.setBusy(true);

      var action;
      if (this.isAdded) {
        // Quitar todas las variantes guardadas de este producto.
        var stored = variantsOf(productId);
        var targets = stored.length ? stored : [variantId];
        action = Promise.all(
          targets.map(function (epi) {
            return removeItem({ empi: productId, epi: parseInt(epi, 10) || undefined, du: payload.du });
          })
        );
      } else {
        action = addItem(payload);
      }

      action
        .then(function () {
          self.dispatchEvent(
            new CustomEvent('wishlist:changed', {
              bubbles: true,
              detail: { productId: productId, added: self.isAdded }
            })
          );
        })
        .catch(function (error) {
          if (error && error.message === 'wishlist-disabled') return;
          // Swym pide identificar al usuario en algunas configuraciones.
          if (error && /login|auth/i.test(String(error.message || error))) {
            window.location.href = LOGIN_URL;
          }
        })
        .then(function () {
          self.setBusy(false);
          self.render();
        });
    }
  }

  if (!window.customElements.get('wishlist-button')) {
    window.customElements.define('wishlist-button', WishlistButton);
  }

  /* Vista compacta de los favoritos en Mi cuenta, compartiendo la lectura del SDK. */
  function safeUrl(value) {
    if (!value) return null;
    try {
      var url = new URL(value, window.location.origin);
      return /^https?:$/.test(url.protocol) ? url.href : null;
    } catch (e) {
      return null;
    }
  }

  class AccountWishlist extends HTMLElement {
    connectedCallback() {
      this._onStoreChange = this.render.bind(this);
      store.subscribers.add(this._onStoreChange);
      this.render();
      load();
      this._loadTimeout = setTimeout(function () {
        if (store.ready) return;
        var status = this.querySelector('[data-wishlist-status]');
        if (status) status.textContent = this.dataset.error;
        this.setAttribute('aria-busy', 'false');
      }.bind(this), 15000);
    }

    disconnectedCallback() {
      store.subscribers.delete(this._onStoreChange);
      clearTimeout(this._loadTimeout);
    }

    render() {
      var list = this.querySelector('[data-wishlist-products]');
      var status = this.querySelector('[data-wishlist-status]');
      var viewAll = this.querySelector('[data-wishlist-view-all]');
      if (!list || !status || !viewAll) return;
      this.setAttribute('aria-busy', store.ready ? 'false' : 'true');
      list.replaceChildren();
      var self = this;
      var count = 0;
      store.items.forEach(function (_, id) {
        if (count >= 8) return;
        var product = store.products.get(id);
        var href = product && safeUrl(product.du);
        if (!href) return;
        var item = document.createElement('li');
        var link = document.createElement('a');
        link.className = 'fs-account__favorite';
        link.href = href;
        link.title = product.dt || self.dataset.productLabel;
        link.setAttribute('aria-label', link.title);
        var src = safeUrl(product.iu);
        if (src) {
          var img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.width = 224;
          img.height = 280;
          img.loading = 'lazy';
          img.addEventListener('error', function () { link.textContent = link.title; });
          link.appendChild(img);
        } else {
          link.textContent = link.title;
        }
        item.appendChild(link);
        list.appendChild(item);
        count++;
      });
      list.hidden = count === 0;
      viewAll.hidden = !store.ready || store.items.size === 0;
      status.hidden = store.ready && !store.error && count > 0;
      status.textContent = !store.ready ? this.dataset.loading :
        store.error || (store.items.size > 0 && count === 0) ? this.dataset.error : this.dataset.empty;
    }
  }

  if (!window.customElements.get('account-wishlist')) {
    window.customElements.define('account-wishlist', AccountWishlist);
  }

  /* ------------------------------------------------------------------ *
   * Icono de cabecera: abre la wishlist de Swym
   * ------------------------------------------------------------------ */
  function bindHeaderIcon() {
    document.querySelectorAll('.wishlist-page-widget').forEach(function (link) {
      if (link.dataset.wishlistBound) return;
      link.dataset.wishlistBound = 'true';
      link.addEventListener('click', function (event) {
        event.preventDefault();
        onSwymReady(function (swat) {
          try {
            swat.ui.open();
          } catch (e) {
            window.location.href = '/pages/swym-wishlist';
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Sin sesión no se ofrece "añadir al carrito" en ninguna parte.
   * El tema ya convierte su propio botón en "Iniciar sesión"; los CTA
   * que inyecta Swym en la página de favoritos se tratan igual.
   * ------------------------------------------------------------------ */
  var CART_CTA_SELECTOR = [
    '.swym-storefront-layout-grid-item-add-to-cart-button',
    '.swym-storefront-layout-add-to-cart-button',
    '.swym-ui-component .swym-add-to-cart-btn'
  ].join(',');

  function isLoggedIn() {
    return document.body.classList.contains('customer-logged-in');
  }

  function convertCartCtas(root) {
    if (isLoggedIn()) return;
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(CART_CTA_SELECTOR).forEach(function (btn) {
      if (btn.dataset.loginCta) return;
      btn.dataset.loginCta = 'true';
      btn.textContent = STRINGS.loginFirst || 'Iniciar sesión';
      btn.disabled = false;
      btn.removeAttribute('disabled');
      btn.style.visibility = 'visible';
      btn.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.location.href = LOGIN_URL;
        },
        true
      );
    });
  }

  function watchCartCtas() {
    if (isLoggedIn()) return;
    convertCartCtas(document);
    var observer = new MutationObserver(function (mutations) {
      var touched = false;
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) touched = true;
        });
      });
      if (touched) convertCartCtas(document);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ *
   * Arranque
   * ------------------------------------------------------------------ */
  function init() {
    bindHeaderIcon();
    watchCartCtas();
    load();
    onSwymReady(function (swat) {
      if (!swat.evtLayer || !swat.JSEvents) return;
      [swat.JSEvents.addedToWishlist, swat.JSEvents.removedFromWishlist].forEach(function (name) {
        if (name) swat.evtLayer.addEventListener(name, function () { load(true); });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Swym dispara sus propios eventos cuando algo cambia desde su UI.
  ['swym:added', 'swym:removed', 'swym:collections-loaded'].forEach(function (name) {
    document.addEventListener(name, function () {
      load(true);
    });
  });

  // Secciones recargadas por el editor de temas o por AJAX.
  document.addEventListener('shopify:section:load', bindHeaderIcon);

  window.ChoonWishlist = {
    load: load,
    add: addItem,
    remove: removeItem,
    has: hasProduct,
    count: totalCount
  };
})();
